import React, { useEffect, useState } from "react";
import {
  Box, Stack, Dialog, CircularProgress, IconButton, Checkbox, Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BtnPrimary from "./PrimaryButton";
import BtnSecondary from "./SecondaryButton";
import EmptyState from "./EmptyState";
import { useNotify } from "./NotificationProvider";
import { getRoles, updateEmployeeRoles } from "../services/roles";
import { tokens, displayFont } from "../theme";

/**
 * Assign roles + admin to an employee. "Admin" = the special permission "All".
 * Persists via PUT /employee/:id/roles-permissions { newRoles, newSpecialPermissions }.
 */
export default function AssignRolesDialog({ open, onClose, employee, onDone }) {
  const notify = useNotify();
  const [roles, setRoles] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !employee) return;
    setSelected(new Set(employee.newRoles || []));
    setIsAdmin((employee.newSpecialPermissions || []).includes("All"));
    (async () => {
      setLoading(true);
      try {
        const res = await getRoles();
        setRoles(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        notify.error(e?.message || "Failed to load roles");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [open, employee]);

  if (!employee) return null;
  const name = `${employee.fname || ""} ${employee.lname || ""}`.trim() || "this employee";

  const toggle = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const save = async () => {
    setSaving(true);
    try {
      const specials = isAdmin ? ["All"] : [];
      const res = await updateEmployeeRoles(employee._id, {
        newRoles: [...selected],
        newSpecialPermissions: specials,
      });
      if (res?.success !== false) {
        notify.success("Roles updated");
        onDone?.();
        onClose();
      } else notify.error(res?.message || "Could not update roles");
    } catch (e) {
      notify.error(e?.message || "Something went wrong");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: "12px", width: "min(32rem,94vw)" } }}>
      <Box sx={{ p: "1.5rem" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb="0.5rem">
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Roles & access</Box>
          <IconButton aria-label="Close" size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "1.25rem" }}>For {name}</Box>

        {/* Admin toggle */}
        <Stack direction="row" alignItems="center" justifyContent="space-between"
          sx={{ p: "1rem 1rem", border: `1px solid ${tokens.border}`, borderRadius: "10px", mb: "1.25rem" }}>
          <Box>
            <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>Administrator</Box>
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Full access to everything in the organisation.</Box>
          </Box>
          <Switch checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />
        </Stack>

        <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text, mb: "0.5rem", opacity: isAdmin ? 0.5 : 1 }}>Roles</Box>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: "2rem" }}><CircularProgress size={22} /></Box>
        ) : roles.length === 0 ? (
          <EmptyState title="No roles defined" description="Create roles in Organisation → Roles & Permissions first." />
        ) : (
          <Box sx={{ maxHeight: "16rem", overflowY: "auto", opacity: isAdmin ? 0.5 : 1, pointerEvents: isAdmin ? "none" : "auto" }}>
            {roles.map((role) => (
              <Stack key={role._id} direction="row" alignItems="flex-start" spacing={1} sx={{ py: 0.5, cursor: "pointer" }} onClick={() => toggle(role._id)}>
                <Checkbox size="small" checked={selected.has(role._id)} sx={{ p: 0.5 }} />
                <Box>
                  <Box sx={{ fontSize: "0.875rem", fontWeight: 500, color: tokens.text }}>{role.name}</Box>
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{role.description}</Box>
                </Box>
              </Stack>
            ))}
          </Box>
        )}

        <Stack direction="row" spacing={1.5} justifyContent="flex-end" mt="1.5rem">
          <BtnSecondary onClick={onClose} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
          <BtnPrimary onClick={save} disabled={saving} sx={{ borderRadius: "8px" }}>
            {saving ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Save"}
          </BtnPrimary>
        </Stack>
      </Box>
    </Dialog>
  );
}
